import {
	ProseMirror,
	ProseMirrorDoc,
	useEditorEffect,
	useEditorEventListener,
} from "@handlewithcare/react-prosemirror";
import { Slice } from "prosemirror-model";
import { type EditorState, NodeSelection } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import React, {
	type PropsWithChildren,
	createElement,
	forwardRef,
	useCallback,
	useEffect,
	useState,
} from "react";

import {
	type DocumentMetadata,
	type PublishSettings,
	makeUrlPrefix,
} from "@moment/api-collab/api-types";
import { schema } from "@moment/api-collab/prosemirror-schema";
import { SCROLL_CONTAINER_ROOT_ID } from "@moment/design-system/ScrollContainer";

import { useEasel } from "~/components/canvas/easel";
import { removeDataSrcImagesFromFragment } from "~/components/canvas/easel/prosemirror/helpers";
import { useRuntime } from "~/components/canvas/easel/runtime/useRuntime";
import {
	focusEditor,
	setTextPlaceholderStateBlurred,
	setTextPlaceholderStateFocused,
} from "~/components/canvas/editor/editor-controls";
import { useLocationHash } from "~/components/canvas/hooks/useLocationHash";
import { useSendToCodeEditorStore } from "~/state/machines/code-editor";
import { LOCAL_WORKSPACE_NAME } from "~/state/types";

import { Image } from "../easel/prosemirror/nodeViews/Image/Image";
import { CodeBlock } from "../easel/prosemirror/nodeViews/codeBlock/CodeBlock";
import { ComputeCell } from "../easel/prosemirror/nodeViews/computeCell/ComputeCell";
import { ToDoItem } from "../easel/prosemirror/nodeViews/toDoItem/ToDoItem";
import { usePageEditorState } from "../easel/usePageEditorState";
import { useComputeCells } from "../hooks/useComputeCells";
import { useEaselCurrentPage } from "../hooks/useEaselCurrentPage";
import { useEaselDeleteComputeCell } from "../hooks/useEaselDeleteComputeCell";
import { useEaselInsertTemplateAtPos } from "../hooks/useEaselInsertTemplateAtPos";
import { useEaselProseMirrorDropHandler } from "../hooks/useEaselProseMirrorDropHandler";
import { useEaselProseMirrorPasteHandler } from "../hooks/useEaselProseMirrorPasteHandler";
import type { ComponentTemplateItem } from "../properties-pane/components-tab/types";
import { BottomMargin } from "./controls/BottomMargin";

export const insertComponentFromTemplateEventId = "moment-insert-component-from-template";

export type EditorProps = PropsWithChildren<{
	document: DocumentMetadata;
	editorState: EditorState;
}>;

export const Editor = forwardRef<
	HTMLDivElement,
	PropsWithChildren<{ publishTemplate?: PublishSettings["template"] }>
>(({ publishTemplate }, editorContainerRef) => {
	const { documentId, pageId } = useEasel();
	const page = useEaselCurrentPage();

	const send = useSendToCodeEditorStore();

	// Hooks
	const hash = useLocationHash();

	// Component state
	const [ranHashEffect, setRanHashEffect] = useState(false);

	useEffect(() => {
		// reset the hash effect when the page changes
		setRanHashEffect(false);
	}, [page?.id]);

	useEffect(() => {
		// Handle hash URL navigation
		if (
			hash === "" ||
			page === undefined ||
			// editorViewRef.current === null ||
			ranHashEffect
		) {
			return;
		}

		const id = hash.startsWith("#") ? hash.slice(1) : hash;
		const elementToScrollTo = document.getElementById(id);
		elementToScrollTo?.scrollIntoView();
		setRanHashEffect(true);
	}, [hash, page, ranHashEffect]);

	const { compiledCells } = useComputeCells(documentId, pageId);

	const insertTemplateAtPos = useEaselInsertTemplateAtPos();

	const deleteComputeCell = useEaselDeleteComputeCell();
	useEditorEventListener("keydown", (view, event) => {
		const sel = view.state.selection;
		if (!(sel instanceof NodeSelection) || sel.node.type !== schema.nodes.compute_cell) {
			return;
		}

		// If a user has a compute cell selected and they press a letter key, we don't want to
		// delete the compute cell.
		if (
			event.key !== "Backspace" &&
			event.key !== "Delete" &&
			event.key !== "ArrowUp" &&
			event.key !== "ArrowDown" &&
			event.key !== "ArrowLeft" &&
			event.key !== "ArrowRight" &&
			!(event.ctrlKey || event.metaKey || event.altKey)
		) {
			event.preventDefault();
			return;
		}

		if (event.key !== "Backspace" && event.key !== "Delete") {
			return;
		}

		const id = sel.node.attrs["id"];
		if (!id || id === "") {
			return;
		}

		void deleteComputeCell(id);
	});

	useEditorEffect(
		(view) => {
			const handler = async (e: unknown) => {
				// NOTE: I am not sure how to make the types work here, so we'll just go with a type
				// assertion.
				const event = e as CustomEvent<ComponentTemplateItem>;
				const template = event.detail;

				view.dispatch(
					insertTemplateAtPos(view.state.tr, template, view.state.selection.$anchor.pos)
				);

				const sel = view.state.selection;
				if (
					sel instanceof NodeSelection &&
					sel.node.type === schema.nodes.compute_cell &&
					sel.node.attrs["id"] &&
					template.requiresCodePane === true
				) {
					send({
						type: "openTab",
						openCodePane: true,
						activePageID: pageId,
						cellID: sel.node.attrs["id"],
					});
				}

				//
				// Focus the editor; user has clicked a button or dragged-and-dropped a component
				// and the editor will therefore not have the focus. `setTimeout` to perform the
				// focus right after the render loop has completed.
				//

				setTimeout(() => view.focus(), 0);
			};

			document.addEventListener(insertComponentFromTemplateEventId, handler);
			return () => document.removeEventListener(insertComponentFromTemplateEventId, handler);
		},
		[compiledCells, insertTemplateAtPos, pageId, send]
	);

	// Focus the editor when it's firt rendered.
	useEditorEffect((view) => {
		view.focus();
	}, []);

	useEditorEffect((view) => {
		// FIXME: Yes yes this is very lame. The alternative is drilling it down into the plugins
		// layer, but that is called in many places which do not know about the publishTemplate.
		if (publishTemplate !== "tech-docs-vitepress") {
			return;
		}
		view.dom.classList.add("no-margin");
	});

	return (
		<>
			<ProseMirrorDoc ref={editorContainerRef} />
			<BottomMargin />

			{/* {isEnabled(Flags.Comments) && (
					<CellCommentBubbles parentEl={editorContainerRef} />
				)} */}
		</>
	);
});

Editor.displayName = "Editor";

const nodeViews = {
	compute_cell: ComputeCell,
	code_block: CodeBlock,
	to_do_item: ToDoItem,
	image: Image,
};

const EditorWrapper: React.FC<EditorProps> = ({ document, editorState, ...props }) => {
	const { workspaceName, documentId, pageId } = useEasel();
	const { applyTransaction } = usePageEditorState(documentId, pageId);

	const decorations = useCallback(
		(state: EditorState) => {
			const decos: Decoration[] = [];
			state.doc.descendants((node, pos) => {
				const linkMark = schema.marks.link.isInSet(node.marks);
				if (linkMark?.attrs["href"].startsWith("/d/")) {
					const urlPrefix = makeUrlPrefix(
						workspaceName,
						documentId,
						document.publishSettings
					);

					const newHref = linkMark.attrs["href"].replace(
						/^\/d\/local\/.*?\//,
						`${urlPrefix}/`
					);

					decos.push(
						Decoration.inline(pos, pos + node.nodeSize, {
							nodeName: "a",
							href: newHref,
							title: linkMark.attrs["title"],
						})
					);
				}
				return true;
			});
			return DecorationSet.create(state.doc, decos);
		},
		[document.publishSettings, documentId, workspaceName]
	);

	const page = useEaselCurrentPage();

	const send = useSendToCodeEditorStore();

	const dropHandler = useEaselProseMirrorDropHandler();
	const pasteHandler = useEaselProseMirrorPasteHandler();

	return (
		<ProseMirror
			key={page?.id}
			state={editorState}
			attributes={{
				id: SCROLL_CONTAINER_ROOT_ID,
				class: "group/prosemirror",
			}}
			decorations={decorations}
			nodeViews={nodeViews}
			handlePaste={pasteHandler}
			transformPasted={(slice, _view) => {
				/* We remove images with data srcs because they can be so large
				 * that the document isn't safe to save in dynamo. Note that
				 * even if we move storage to a different store we still probably
				 * want to remove very large data src uris since they will make
				 * document loading difficult. Instead we shuold be using our
				 * image uploader to convert these to images in s3 and replace
				 * them with an s3 url.
				 */
				const f = removeDataSrcImagesFromFragment(slice.content);
				return new Slice(f, slice.openStart, slice.openEnd);
			}}
			editable={() => {
				/* If we are in the local workspace, we are always editable because
				 * it means the files are on disk.
				 */
				return workspaceName === LOCAL_WORKSPACE_NAME;
			}}
			handleDrop={dropHandler}
			handleDOMEvents={{
				focus: (view, _event) => {
					//
					// VERY IMPORTANT: Do NOT race with page title changes. This is among the
					// lamest things that we have ever done. Here's what happens:
					//
					//   1. Click "New Page."
					//   2. Make a title for the new page.
					//   3. Do NOT press enter or click anywhere else.
					//   4. While the title is still being edited, click the canvas.
					//   5. We will replace the placeholder and then immediately a bunch of
					//      other transactions will be fired off. They do things like
					//      un-highlight comments.
					//   6. Because we just replaced the document placeholder, all those
					//      transactions will conflict.
					//
					// To avoid all this, we simply replace the placeholder in a few hundred
					// milliseconds.
					//

					setTimeout(() => {
						const { tr } = view.state;

						focusEditor({
							view: view as unknown as EditorView,
							send,
							activePageID: page?.id,
						});

						// Assemble and dispatch a single transaction
						setTextPlaceholderStateFocused(tr);
						view.dispatch(tr);
					}, 200);
					return false;
				},
				blur: (view) => {
					const { tr } = view.state;

					// Assemble and dispatch a single transaction
					setTextPlaceholderStateBlurred(tr);
					view.dispatch(tr);
					return false;
				},
				mouseover: (_view, _event) => {
					// controlSetters.mouse(event);
					return false;
				},
			}}
			handleClickOn={(view, pos, node) => {
				if (node.type === schema.nodes.compute_cell) {
					return true;
				}

				return false;
			}}
			dispatchTransaction={(tr) => {
				// pass transactions back up to Easel
				// this is needed to make sure that the editor state is updated when things like "selections" are updated
				void applyTransaction({ createTransaction: () => tr });
			}}
		>
			{props.children}
		</ProseMirror>
	);
};

const BigRichTextEditorInterop: React.FC<
	{
		document: DocumentMetadata;
		editorState?: EditorState;
	} & PropsWithChildren
> = ({ document, editorState, ...props }) => {
	const runtime = useRuntime();

	return runtime && editorState
		? createElement(EditorWrapper, { ...props, document, editorState })
		: null;
};

export const BigRichTextEditor = React.memo(BigRichTextEditorInterop);
